import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, User } from 'pi-kiosk-shared/ui/Icon';
import { isCustomerBrowseEnabled } from '../config/customerFeatureFlags.js';
import { isGuestAuthPath } from '../features/auth/routing/isGuestAuthPath.js';
import { useCustomerFeatureFlags } from '../features/flags/useCustomerFeatureFlags.js';
import { isGloballySignedInChrome } from '../features/hub/customerHubPaths.js';
import { useCustomerSession } from '../shared/session/CustomerSessionProvider.js';
import { useTenantCode } from '../shared/tenant/TenantProvider.js';
import { resolveBottomNavActiveTab } from './bottomNavActiveTab.js';
import { CUSTOMER_SHELL_ICON_BUTTON_CLASS } from './customerTopBarStyles.js';

export interface CustomerProfileButtonProps {
  readonly className?: string;
  /** Whether CustomerProfileSheet is open (drives aria-expanded). */
  readonly profileOpen?: boolean;
  /** Toggles CustomerProfileSheet (guest: Sign in / Register; signed-in: Account / Sign out). */
  readonly onOpenProfile?: () => void;
}

/**
 * Retail V1 shell profile chrome — icon-only user in the end cluster (parity
 * with Cart on non-Home journeys), including guest-auth screens.
 *
 * HUX-01: omitted from root `/browse` first-row; open via MoreDrawer / `/more`
 * / `/account/**` secondary (sheet openers registered on MoreShellContext).
 *
 * Click toggles the chrome profile sheet (account CTAs) — never navigates.
 * Re-click while open dismisses (parity with panel ✕). Guest CTAs inside the
 * sheet park return + go to sign-in / register. Pre-hydrate with no session on
 * auth-gated chrome → click is a no-op until `/me` settles. Platform public
 * browse/scan/shops skip the wait (no aria-busy; guest sheet opens immediately).
 * Language / appearance / guest cookies: Settings gear (CustomerSettingsSheet).
 */
export function CustomerProfileButton({
  className,
  profileOpen = false,
  onOpenProfile,
}: CustomerProfileButtonProps): JSX.Element {
  const { t } = useTranslation('customer');
  const tenantCode = useTenantCode();
  const location = useLocation();
  const { session, sessionHydrated } = useCustomerSession();
  const globallySignedIn = isGloballySignedInChrome({ session });
  const activeTab = resolveBottomNavActiveTab(location.pathname, tenantCode);
  const { flags } = useCustomerFeatureFlags();
  const browseNavEnabled = isCustomerBrowseEnabled(flags);
  const onShopRoute = activeTab === 'shop';
  const onGuestAuth = isGuestAuthPath(location.pathname);
  /** Platform public discovery chrome — no auth guard; avoid 12s aria-busy on cold /me. */
  const onPublicBrowseChrome =
    (browseNavEnabled && activeTab === 'browse') || activeTab === 'scan' || activeTab === 'shops';

  const classNames = [CUSTOMER_SHELL_ICON_BUTTON_CLASS, className].filter(Boolean).join(' ');
  /** Primary chrome test id; shop-route secondary for Appendix J / shop header wiring. */
  const shopSecondaryTestId =
    onShopRoute && !onGuestAuth ? 'customer-shop-header-account' : undefined;

  const openProfileSheet = useCallback(() => {
    // SoftRace: live session opens even if hydrated flag lags.
    if (session !== null) {
      onOpenProfile?.();
      return;
    }
    // Pre-hydrate guest on auth-gated chrome — do not open until `/me` settles.
    if (!sessionHydrated && !onPublicBrowseChrome) {
      return;
    }
    onOpenProfile?.();
  }, [onOpenProfile, onPublicBrowseChrome, session, sessionHydrated]);

  const ariaLabel = globallySignedIn
    ? t('chrome.profile.openAria', { defaultValue: t('journey.myAccount') })
    : t('chrome.profile.signInAria', { defaultValue: t('journey.signIn') });
  const pendingHydrate =
    !globallySignedIn && !sessionHydrated && !onPublicBrowseChrome;

  return (
    <button
      type="button"
      className={classNames}
      aria-label={ariaLabel}
      title={ariaLabel}
      aria-expanded={profileOpen}
      aria-haspopup="dialog"
      aria-busy={pendingHydrate || undefined}
      data-testid="customer-shell-profile"
      data-auth-state={globallySignedIn ? 'signed-in' : 'guest'}
      data-session-pending={pendingHydrate ? 'true' : undefined}
      data-on-guest-auth={!globallySignedIn && onGuestAuth ? 'true' : undefined}
      data-shop-testid={shopSecondaryTestId}
      onClick={openProfileSheet}
    >
      <Icon icon={User} size={20} aria-hidden={true} />
      {shopSecondaryTestId !== undefined ? (
        <span className="sr-only" data-testid={shopSecondaryTestId}>
          {ariaLabel}
        </span>
      ) : null}
    </button>
  );
}
